"use client";

import React, { createContext, useContext, useRef } from "react";
import { createStore, useStore } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// ═══════════════════════════════════════════════════════════════
// Cart Item Type
// ═══════════════════════════════════════════════════════════════
export interface CartItem {
    id: string;
    name: string;
    price: number;
    image: string;
    quantity: number;
    maxStock?: number;
    variant?: string;
    variantId?: string;
    variantOptions?: Record<string, string>;
    slug?: string;
    currency?: string;
}

// ═══════════════════════════════════════════════════════════════
// Cart Store Interface
// ═══════════════════════════════════════════════════════════════
interface CartState {
    items: CartItem[];
    isOpen: boolean;
    siteSlug: string;

    // Actions
    addItem: (item: Omit<CartItem, "quantity"> & { quantity?: number }) => void;
    removeItem: (id: string) => void;
    updateQuantity: (id: string, quantity: number) => void;
    clearCart: () => void;
    openCart: () => void;
    closeCart: () => void;
    toggleCart: () => void;

    // Getters
    getSubtotal: () => number;
    getTotalItems: () => number;
}

// ═══════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════
export const FREE_SHIPPING_THRESHOLD = 5000;
export const DEFAULT_SHIPPING_COST = 500;

// ═══════════════════════════════════════════════════════════════
// Create Store Factory (per site)
// ═══════════════════════════════════════════════════════════════
const createCartStore = (siteSlug: string) => {
    return createStore<CartState>()(
        persist(
            (set, get) => ({
                items: [],
                isOpen: false,
                siteSlug,

                addItem: (item) => {
                    set((state) => {
                        // Use composite key: productId + variantId for deduplication
                        const cartKey = item.variantId ? `${item.id}::${item.variantId}` : item.id;
                        const existingItem = state.items.find((i) => {
                            const existingKey = i.variantId ? `${i.id}::${i.variantId}` : i.id;
                            return existingKey === cartKey;
                        });

                        if (existingItem) {
                            const newQuantity = existingItem.quantity + (item.quantity || 1);
                            const maxAllowed = item.maxStock || 99;

                            return {
                                items: state.items.map((i) => {
                                    const iKey = i.variantId ? `${i.id}::${i.variantId}` : i.id;
                                    return iKey === cartKey
                                        ? { ...i, quantity: Math.min(newQuantity, maxAllowed) }
                                        : i;
                                }),
                                isOpen: true,
                            };
                        }

                        return {
                            items: [
                                ...state.items,
                                {
                                    id: cartKey, // Use composite key as the item ID
                                    name: item.name,
                                    price: Number(item.price) || 0,
                                    image: item.image,
                                    quantity: item.quantity || 1,
                                    maxStock: item.maxStock,
                                    variant: item.variant,
                                    variantId: item.variantId,
                                    variantOptions: item.variantOptions,
                                    slug: item.slug,
                                    currency: item.currency,
                                },
                            ],
                            isOpen: true,
                        };
                    });
                },

                removeItem: (id) => {
                    set((state) => ({
                        items: state.items.filter((item) => item.id !== id),
                    }));
                },

                updateQuantity: (id, quantity) => {
                    set((state) => {
                        if (quantity <= 0) {
                            return { items: state.items.filter((item) => item.id !== id) };
                        }

                        return {
                            items: state.items.map((item) => {
                                if (item.id === id) {
                                    const maxAllowed = item.maxStock || 99;
                                    return { ...item, quantity: Math.min(quantity, maxAllowed) };
                                }
                                return item;
                            }),
                        };
                    });
                },

                clearCart: () => set({ items: [] }),
                openCart: () => set({ isOpen: true }),
                closeCart: () => set({ isOpen: false }),
                toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),

                getSubtotal: () => {
                    const { items } = get();
                    return items.reduce((total, item) => total + (Number(item.price) || 0) * item.quantity, 0);
                },

                getTotalItems: () => {
                    const { items } = get();
                    return items.reduce((total, item) => total + item.quantity, 0);
                },
            }),
            {
                // ✅ UNIQUE KEY PER STORE
                name: `ecommaps-cart-${siteSlug}`,
                storage: createJSONStorage(() => localStorage),
                partialize: (state) => ({ items: state.items }),
            }
        )
    );
};

// ═══════════════════════════════════════════════════════════════
// Cart Context
// ═══════════════════════════════════════════════════════════════
type CartStore = ReturnType<typeof createCartStore>;

const CartContext = createContext<CartStore | null>(null);

// ═══════════════════════════════════════════════════════════════
// Cart Provider - Must wrap tenant pages
// ═══════════════════════════════════════════════════════════════
interface CartProviderProps {
    children: React.ReactNode;
    siteSlug: string;
}

export function CartProvider({ children, siteSlug }: CartProviderProps) {
    const storeRef = useRef<CartStore | null>(null);
    const slugRef = useRef<string>(siteSlug);

    // Recreate store if slug changes (critical for SPA navigation between stores)
    if (!storeRef.current || slugRef.current !== siteSlug) {
        storeRef.current = createCartStore(siteSlug || "default");
        slugRef.current = siteSlug;
    }

    return React.createElement(
        CartContext.Provider,
        { value: storeRef.current },
        children
    );
}

// ═══════════════════════════════════════════════════════════════
// useCart Hook - Type-safe store access with fallback for editor
// ═══════════════════════════════════════════════════════════════

// No-op fallback for when cart is used outside provider (e.g., in editor preview)
const noOpCart = {
    items: [] as CartItem[],
    isOpen: false,
    addItem: () => { },
    removeItem: () => { },
    updateQuantity: () => { },
    clearCart: () => { },
    openCart: () => { },
    closeCart: () => { },
    toggleCart: () => { },
    getSubtotal: () => 0,
    getTotalItems: () => 0,
};

export function useCart() {
    const store = useContext(CartContext);

    // Return safe fallback if no provider (e.g., in editor preview)
    if (!store) {
        return noOpCart;
    }

    const items = useStore(store, (s) => s.items);
    const isOpen = useStore(store, (s) => s.isOpen);
    const addItem = useStore(store, (s) => s.addItem);
    const removeItem = useStore(store, (s) => s.removeItem);
    const updateQuantity = useStore(store, (s) => s.updateQuantity);
    const clearCart = useStore(store, (s) => s.clearCart);
    const openCart = useStore(store, (s) => s.openCart);
    const closeCart = useStore(store, (s) => s.closeCart);
    const toggleCart = useStore(store, (s) => s.toggleCart);
    const getSubtotal = useStore(store, (s) => s.getSubtotal);
    const getTotalItems = useStore(store, (s) => s.getTotalItems);

    return {
        items,
        isOpen,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        openCart,
        closeCart,
        toggleCart,
        getSubtotal,
        getTotalItems,
    };
}

// ═══════════════════════════════════════════════════════════════
// Helper: Calculate Shipping Based on Wilaya
// ═══════════════════════════════════════════════════════════════
export const getShippingCost = (wilayaCode: string, subtotal: number): number => {
    if (subtotal >= FREE_SHIPPING_THRESHOLD) return 0;

    const premiumWilayas = ["16", "31", "25"];
    if (premiumWilayas.includes(wilayaCode)) return 400;

    return DEFAULT_SHIPPING_COST;
};
